import { NormalizedNumber, Percentage } from '@sparkdotfi/common-universal'

import { CapAutomatorConfig } from '../../data/cap-automator/types'
import { ChartResultWrapper, createChartConfig } from '../charts/components/ChartResultWrapper'
import { RateChart } from '../charts/components/rate-chart/RateChart'
import { EmptyStatusPanel } from './components/EmptyStatusPanel'
import { Header } from './components/Header'
import { InfoTilesGrid } from './components/InfoTilesGrid'
import { SparkRewardsBadge } from './components/SparkRewardsBadge'
import { SparkRewardsInfoTile } from './components/SparkRewardsInfoTile'
import { StatusPanelGrid } from './components/StatusPanelGrid'
import { Subheader } from './components/Subheader'
import { StatusIcon } from './components/status-icon/StatusIcon'
import { formatPercentage } from '@/domain/common/format'
import { SimplifiedQueryResult } from '@/domain/common/query'
import { Token } from '@/domain/types/Token'
import { MarketSparkRewards } from '@/features/spark-rewards/types'
import { SupplyAvailabilityStatus } from '@/features/sparklend/common/data/market-info/reserve-status'
import { InfoTile } from '@/features/sparklend/market-details/components/info-tile/InfoTile'
import { MarketRateChartData } from '@/features/sparklend/market-details/data/market-charts/types'
import { ApyTooltip } from '@/ui/molecules/apy-tooltip/ApyTooltip'
import { CooldownTimer } from '@/ui/molecules/cooldown-timer/CooldownTimer'
import { TokenAmountTooltip } from '@/ui/organisms/token-amount-tooltip/TokenAmountTooltip'
import { UsdAmountTooltip } from '@/ui/organisms/usd-amount-tooltip/UsdAmountTooltip'
import { testIds } from '@/ui/utils/testIds'

interface SupplyStatusPanelProps {
  status: SupplyAvailabilityStatus
  token: Token
  totalSupplied: NormalizedNumber
  apy: Percentage | undefined
  supplyCap?: NormalizedNumber
  capAutomatorInfo?: CapAutomatorConfig
  sparkRewards: MarketSparkRewards[]
  instantlyAvailableToSupply?: NormalizedNumber
  supplyRateHistoricResult?: SimplifiedQueryResult<MarketRateChartData>
}

export function SupplyStatusPanel({
  status,
  token,
  totalSupplied,
  supplyCap,
  apy,
  capAutomatorInfo,
  sparkRewards,
  instantlyAvailableToSupply,
  supplyRateHistoricResult,
}: SupplyStatusPanelProps) {
  if (status === 'no') {
    return <EmptyStatusPanel status={status} variant="supply" />
  }

  const supplyRateChartConfig =
    supplyRateHistoricResult &&
    createChartConfig({
      component: RateChart,
      props: { data: supplyRateHistoricResult.data ?? [], metricDisplayName: 'Supply Rate (APR)' },
      isError: supplyRateHistoricResult.isError,
      isPending: supplyRateHistoricResult.isPending,
    })

  return (
    <StatusPanelGrid>
      <StatusIcon status={status} />
      <Header status={status} variant="supply" />
      <SparkRewardsBadge sparkRewards={sparkRewards} />
      <Subheader status={status} />
      <InfoTilesGrid>
        <InfoTile>
          <InfoTile.Label>Total supplied</InfoTile.Label>
          <InfoTile.Value>
            <TokenAmountTooltip
              token={token}
              amount={totalSupplied}
              options={{ style: 'compact', showSymbol: 'always' }}
            />
          </InfoTile.Value>
          <InfoTile.ComplementaryLine>
            <UsdAmountTooltip token={token} amount={totalSupplied} style="compact" />
          </InfoTile.ComplementaryLine>
        </InfoTile>
        <InfoTile>
          <InfoTile.Label>
            <ApyTooltip variant="supply">Deposit APY</ApyTooltip>
          </InfoTile.Label>
          <InfoTile.Value>{formatPercentage(apy)}</InfoTile.Value>
        </InfoTile>
        <SparkRewardsInfoTile sparkRewards={sparkRewards} />

        {supplyCap && <SupplyCapInfoTile token={token} capAutomatorInfo={capAutomatorInfo} supplyCap={supplyCap} />}
        {instantlyAvailableToSupply && (
          <InstantlyAvailableToSupplyInfoTile
            token={token}
            capAutomatorInfo={capAutomatorInfo}
            instantlyAvailableToSupply={instantlyAvailableToSupply}
          />
        )}
      </InfoTilesGrid>

      {supplyRateChartConfig && (
        <div className="col-span-3 mt-6 sm:col-span-2 sm:col-start-2 sm:mt-10">
          <ChartResultWrapper {...supplyRateChartConfig} height={200} />
        </div>
      )}
    </StatusPanelGrid>
  )
}

interface SupplyCapInfoTileProps {
  token: Token
  capAutomatorInfo?: CapAutomatorConfig
  supplyCap: NormalizedNumber
}

function SupplyCapInfoTile({ token, capAutomatorInfo, supplyCap }: SupplyCapInfoTileProps) {
  const maxCap = capAutomatorInfo?.maxCap ?? supplyCap

  return (
    <InfoTile>
      <InfoTile.Label>Supply cap</InfoTile.Label>
      <InfoTile.Value data-testid={testIds.marketDetails.supplyStatusPanel.cap}>
        <TokenAmountTooltip token={token} amount={maxCap} options={{ style: 'compact', showSymbol: 'always' }} />
      </InfoTile.Value>
      <InfoTile.ComplementaryLine>
        <UsdAmountTooltip token={token} amount={maxCap} style="compact" />
      </InfoTile.ComplementaryLine>
    </InfoTile>
  )
}

interface InstantlyAvailableToSupplyInfoTileProps {
  token: Token
  instantlyAvailableToSupply: NormalizedNumber
  capAutomatorInfo?: CapAutomatorConfig
}

function InstantlyAvailableToSupplyInfoTile({
  token,
  capAutomatorInfo,
  instantlyAvailableToSupply,
}: InstantlyAvailableToSupplyInfoTileProps) {
  return (
    <InfoTile>
      <InfoTile.Label>Instantly available to supply</InfoTile.Label>
      <InfoTile.Value data-testid={testIds.marketDetails.supplyStatusPanel.instantlyAvailableToSupply}>
        <TokenAmountTooltip
          token={token}
          amount={instantlyAvailableToSupply}
          options={{ style: 'compact', showSymbol: 'always' }}
        />
        {capAutomatorInfo && (
          <CooldownTimer
            renewalPeriod={capAutomatorInfo.increaseCooldown}
            latestUpdateTimestamp={capAutomatorInfo.lastIncreaseTimestamp}
            token={token}
            gap={capAutomatorInfo.gap}
            cooldownOverContent={
              <>The available supply is constrained by the instant supply cap, which may be adjusted at any time. </>
            }
            cooldownActiveContent={
              <>
                The supply is constrained by the instant supply cap, which has a renewal time of{' '}
                {secondsToHours(capAutomatorInfo.increaseCooldown)} hours.{' '}
              </>
            }
          />
        )}
      </InfoTile.Value>
      <InfoTile.ComplementaryLine>
        <UsdAmountTooltip token={token} amount={instantlyAvailableToSupply} style="compact" />
      </InfoTile.ComplementaryLine>
    </InfoTile>
  )
}

function secondsToHours(seconds: number) {
  return Math.floor(seconds / 3600)
}
